import {
  countDaysUntil,
  getCurrencyCode,
  mscGet,
  saltedHash,
  sanitizeAnalyticsValue,
} from '@msc-b2c/shared-utils';
import {
  CabinI,
  ClickCTAParams,
  DiningReservationAnalyticsEvent,
  DiningReservationAnalyticsParams,
  DiningReservationAnalyticsReservationPoint,
  DiningReservationAnalyticsReservationType,
  DiningReservationAnalyticsStepName,
  GetBookingMyMscResponse,
  RetrieveVoyagerClubInfoMembership,
  RetrieveVoyagerClubInfoMyMSCResponse,
} from '@msc-b2c/interfaces/interfaces';
import { CabinCategoryCfmModel, CartItem, PillConfigurations } from '@msc-b2c/interfaces/types';
import {
  getAlreadyBookedPassengerId,
  getPriceFromPriceBasis,
} from '../postbookingresults/utils/PostBookingResultsUtilities';
import {
  cabinOfTheLoggedUser,
  decodeHTMLEntities,
  getCabinIdOfTheLoggedUser,
  getExperienceTitleFallback,
  getItineraryNameByCanvas,
  getNumberOfAdultsInCabin,
  getShipCodeFromCruiseIdPA,
} from './myAreaUtils';
import {
  BookingStatusEnum,
  DiningFlowEntryPoint,
  LoginStatusAnalyticsEnum,
  OBSTypesEnum,
  TransactionStatusEnum,
  WebCheckInStatusEnum,
} from '@msc-b2c/interfaces/enums';
import dayjs from 'dayjs';
import {
  AnalyticsCard,
  getCardUnitPrice,
  getDiscountValue,
  getPriceDiscountValue,
  getShopResponseUnitPriceDetails,
  consumePendingBookingStatusAnalytics,
  getViewItemPrice,
  ShopAnalyticsResponse,
  getActiveCruiseCabinCount,
  getSeasonFromDepartureDate,
  EnrichedAnalyticsCard,
  defineItemListName,
  resolveItemListName,
  isOBSObjI,
  buildItemListNameValue,
  getSelectedPassengerQuantityFromSelection,
  getSelectedPassengerQuantityFromCart,
} from './analyticsUtils';

const cardTypeMapping: Record<string, { synonyms: string[]; code: string }> = {
  visa: { synonyms: ['visa'], code: 'vis' },
  mastercard: {
    synonyms: ['MASTERCARD', 'mastercard', 'master card', 'master-card', 'MCI', 'mci'],
    code: 'eca',
  },
  amex: { synonyms: ['amex', 'american express'], code: 'amx' },
  dinersclub: { synonyms: ['diners club', 'diners'], code: 'din' },
  discover: { synonyms: ['discover'], code: 'dis' },
  jcb: { synonyms: ['jcb'], code: 'jcb' },
  maestro: { synonyms: ['maestro'], code: 'mau' },
  dankort: { synonyms: ['dankort'], code: 'dn' },
  cup: { synonyms: ['cup', 'china union pay'], code: 'cup' },
};

type PaymentDataType = {
  cardInfo?: {
    brand?: string;
  };
};

export function getPaymentTypeFromSession(): string {
  const sessionPaymentData = mscGet<PaymentDataType>('session', 'paymentData');
  const brand = sessionPaymentData?.cardInfo?.brand;
  if (!brand) return '';
  const lowercasedBrand = brand.toLowerCase();
  for (const [_key, value] of Object.entries(cardTypeMapping)) {
    if (value.synonyms.some((synonym) => lowercasedBrand.includes(synonym))) {
      return value.code;
    }
  }
  return lowercasedBrand;
}

let previousCardsForList: AnalyticsCard[] | undefined = [];
let previousCardsForListPath = '';
let previousCardsForItem: AnalyticsCard[] | AnalyticsCard | undefined = [];
type DiningReservationAnalyticsRestaurant = {
  code?: string;
  timeslots?: { isReserved?: boolean }[];
};
type DiningReservationAnalyticsSelection = {
  restaurantCode?: string;
  time?: string;
  dayNumber?: number;
};

function joinDiningAnalyticsValues(values: (string | number | undefined)[]): string {
  return values
    .filter((value): value is string | number => value !== undefined && value !== '')
    .map(String)
    .join(':');
}

function formatDiningAnalyticsTime(time?: string): string {
  if (!time) {
    return '';
  }

  const [hours = '', minutes = ''] = time.split(':');
  return `${hours.padStart(2, '0')}h${minutes.padStart(2, '0')}`;
}

function getDiningSlotAnalyticsValues(
  restaurants: DiningReservationAnalyticsRestaurant[] = []
): Partial<DiningReservationAnalyticsParams> {
  const restaurantsWithCode = restaurants.filter((restaurant) => !!restaurant.code);

  return {
    reservation_available: joinDiningAnalyticsValues(
      restaurantsWithCode.map((restaurant) => restaurant.code)
    ),
    reservation_total_slots: joinDiningAnalyticsValues(
      restaurantsWithCode.map((restaurant) => restaurant.timeslots?.length ?? 0)
    ),
    reservation_available_slots: joinDiningAnalyticsValues(
      restaurantsWithCode.map(
        (restaurant) =>
          restaurant.timeslots?.filter((slot) => slot.isReserved === false).length ?? 0
      )
    ),
    reservation_unavailable_slots: joinDiningAnalyticsValues(
      restaurantsWithCode.map((restaurant) => {
        const timeslots = restaurant.timeslots ?? [];
        const availableSlots = timeslots.filter((slot) => slot.isReserved === false);
        return timeslots.length - availableSlots.length;
      })
    ),
  };
}

function getDiningAnalyticsEventName(
  entryPoint: DiningFlowEntryPoint,
  isReservationUpdate: boolean
): DiningReservationAnalyticsEvent {
  if (
    (entryPoint === DiningFlowEntryPoint.ALREADY_PURCHASED ||
      entryPoint === DiningFlowEntryPoint.CART) &&
    isReservationUpdate
  ) {
    return 'reservation_update';
  }

  return 'reservation';
}

function getDiningAnalyticsReservationPoint(
  entryPoint: DiningFlowEntryPoint
): DiningReservationAnalyticsReservationPoint {
  if (entryPoint === DiningFlowEntryPoint.ALREADY_PURCHASED) {
    return 'already purchase';
  }

  if (entryPoint === DiningFlowEntryPoint.CART) {
    return 'cart';
  }

  return 'product card';
}

function getDiningAnalyticsReservationType(
  maximumSlotsBookable: number
): DiningReservationAnalyticsReservationType {
  return maximumSlotsBookable <= 1 ? 'restaurant Single item' : 'restaurant Bundle';
}

function getDiningAnalyticsStepNumber(
  entryPoint: DiningFlowEntryPoint,
  stepName: DiningReservationAnalyticsStepName
): string {
  const productStepNumbers: Record<DiningReservationAnalyticsStepName, string> = {
    'begin reservation': '1',
    'select day': '2',
    'select timeslot': '3',
    confirmation: '4',
  };
  const alreadyPurchasedStepNumbers: Partial<Record<DiningReservationAnalyticsStepName, string>> = {
    'select day': '1',
    'select timeslot': '2',
    confirmation: '3',
  };

  if (entryPoint === DiningFlowEntryPoint.PRODUCT_CARD) {
    return productStepNumbers[stepName];
  }

  return alreadyPurchasedStepNumbers[stepName] ?? productStepNumbers[stepName];
}

function getDiningAnalyticsSelectedReservationValues(
  selectedReservations: DiningReservationAnalyticsSelection[] = []
): Partial<DiningReservationAnalyticsParams> {
  return {
    selected_reservation_codes: joinDiningAnalyticsValues(
      selectedReservations.map((reservation) => reservation.restaurantCode)
    ),
    selected_timeslots: joinDiningAnalyticsValues(
      selectedReservations.map((reservation) => formatDiningAnalyticsTime(reservation.time))
    ),
    selected_day: joinDiningAnalyticsValues(
      selectedReservations.map((reservation) => reservation.dayNumber)
    ),
  };
}

export function triggerDiningReservationStepEvent(params: {
  entryPoint: DiningFlowEntryPoint;
  isReservationUpdate?: boolean;
  maximumSlotsBookable: number;
  bookingNumber?: string;
  itemCode?: string;
  itemName?: string;
  stepName: DiningReservationAnalyticsStepName;
  selectedDay?: string;
  selectedDays?: (string | number)[];
  availabilityRestaurants?: DiningReservationAnalyticsRestaurant[];
  selectedReservations?: DiningReservationAnalyticsSelection[];
}): void {
  const {
    entryPoint,
    isReservationUpdate = false,
    maximumSlotsBookable,
    bookingNumber,
    itemCode,
    itemName,
    stepName,
    selectedDay,
    selectedDays,
    availabilityRestaurants,
    selectedReservations,
  } = params;

  if (
    entryPoint !== DiningFlowEntryPoint.PRODUCT_CARD &&
    entryPoint !== DiningFlowEntryPoint.ALREADY_PURCHASED &&
    entryPoint !== DiningFlowEntryPoint.CART
  ) {
    return;
  }

  if (stepName === 'begin reservation' && entryPoint !== DiningFlowEntryPoint.PRODUCT_CARD) {
    return;
  }

  const availabilityData = availabilityRestaurants
    ? getDiningSlotAnalyticsValues(availabilityRestaurants)
    : {};
  const selectedReservationData = selectedReservations
    ? getDiningAnalyticsSelectedReservationValues(selectedReservations)
    : {};
  const selectedDaysData = selectedDays
    ? { selected_day: joinDiningAnalyticsValues(selectedDays) }
    : {};
  const shouldUseSelectedDayOverride =
    !selectedReservationData.selected_day &&
    !selectedDaysData.selected_day &&
    selectedDay !== undefined &&
    selectedDay !== '';

  triggerDiningReservationEvent({
    event: getDiningAnalyticsEventName(entryPoint, isReservationUpdate),
    reservation_point: getDiningAnalyticsReservationPoint(entryPoint),
    reservation_type: getDiningAnalyticsReservationType(maximumSlotsBookable),
    booking_id: bookingNumber ?? '',
    step_name: stepName,
    step_number: getDiningAnalyticsStepNumber(entryPoint, stepName),
    item_id: sanitizeAnalyticsValue(itemCode),
    item_name: sanitizeAnalyticsValue(itemName),
    ...availabilityData,
    ...selectedReservationData,
    ...selectedDaysData,
    ...(stepName !== 'begin reservation' &&
      stepName !== 'select day' &&
      shouldUseSelectedDayOverride && { selected_day: selectedDay }),
  });
}

function getOnboardServiceAnalyticsQuantities(onboardServices: { type?: string }[] = []) {
  const beverages = onboardServices.filter((obs) => obs.type === OBSTypesEnum.BEVERAGES).length;
  const specialtyRestaurants = onboardServices.filter(
    (obs) => obs.type === OBSTypesEnum.RESTAURANTS
  ).length;
  const spaBeauty = onboardServices.filter((obs) => obs.type === OBSTypesEnum.SPA).length;

  return {
    beverages,
    specialtyRestaurants,
    spaBeauty,
    internetPhotosAndOthers: onboardServices.length - beverages - specialtyRestaurants - spaBeauty,
  };
}

const getAgeGroup = (birthDateString?: string): string => {
  if (!birthDateString) return '';
  const birthDate = new Date(birthDateString);
  const today = new Date();

  let age = today.getFullYear() - birthDate.getFullYear();

  const isBirthdayPassed =
    today.getMonth() > birthDate.getMonth() ||
    (today.getMonth() === birthDate.getMonth() && today.getDate() >= birthDate.getDate());
  if (!isBirthdayPassed) {
    age--;
  }

  if (age < 18) return '18-';
  if (age >= 18 && age <= 24) return '18 to 24';
  if (age >= 25 && age <= 34) return '25 to 34';
  if (age >= 35 && age <= 44) return '35 to 44';
  if (age >= 45 && age <= 54) return '45 to 54';
  if (age >= 55 && age <= 64) return '55 to 64';
  if (age >= 65) return '65+';

  return '';
};

function getPromoBadgeFromCart(code: string | undefined): string {
  if (!code) return '';
  const cart = mscGet<CartItem[]>('session', 'cart') ?? [];
  if (!Array.isArray(cart)) return '';
  return cart.find((cartItem) => cartItem?.code === code)?.promoBadges ?? '';
}

function getPageListIndex(card: AnalyticsCard | undefined): number | undefined {
  if (!card) {
    return 1;
  }

  const pageListCard = previousCardsForList?.find((listCard) => listCard?.code === card.code);
  return pageListCard?.index ?? card.index;
}

function getExperienceTitle(
  booking?: GetBookingMyMscResponse,
  cabinDetails?: CabinCategoryCfmModel | null
): string {
  const experienceTitle =
    cabinDetails?.experienceCf?.experienceTitle?.trim() || getExperienceTitleFallback(booking);

  return sanitizeAnalyticsValue(experienceTitle);
}

export function triggerViewItemList(
  cards: EnrichedAnalyticsCard[] | undefined,
  booking: GetBookingMyMscResponse,
  extraData?: {
    carousel_name?: string;
  } | null,
  cabinDetails?: CabinCategoryCfmModel | null,
  membershipData?: RetrieveVoyagerClubInfoMyMSCResponse | null
) {
  const currentPath = window.location.pathname;
  if (
    JSON.stringify(cards) !== JSON.stringify(previousCardsForList) ||
    currentPath !== previousCardsForListPath
  ) {
    const cabinIDOfTheLoggedUser = getCabinIdOfTheLoggedUser(booking);
    const cabinLoggedUser: CabinI | undefined = cabinOfTheLoggedUser(
      booking?.cabins,
      cabinIDOfTheLoggedUser
    );
    const numberOfAdults = getNumberOfAdultsInCabin(cabinLoggedUser?.passengers, booking);
    previousCardsForList = cards;
    previousCardsForListPath = currentPath;
    window.didomiOnReady = window.didomiOnReady || [];
    console.log('Didomi Wrapper triggerViewItemList - About to push callback');
    window.didomiOnReady.push(() => {
      console.log('Didomi Wrapper triggerViewItemListEvent called - Didomi is ready');
      console.log('didomiVendorsEnabled =', window.didomiState?.didomiVendorsEnabled);
      const ecommerceNullData = { ecommerce: null };
      console.log('Data to be pushed:', ecommerceNullData);
      window.adobeDataLayer?.push(ecommerceNullData);
      const viewItemListData = {
        event: 'view_item_list',
        area_of_payment: 'personal area',
        ecommerce: {
          items: [
            // loop through the card array and create an object for each card
            ...(cards || []).map((card) => {
              const isObsCard = isOBSObjI(card);
              const effectiveItemCategory2 = card?.itemCategory2?.length
                ? card.itemCategory2
                : isObsCard
                ? card.type.code
                : 'excursions';
              const discountValue = getDiscountValue(card, true);
              const alreadyBookedPassengerQuantity = getAlreadyBookedPassengerId(
                card,
                booking,
                !isObsCard
              ).length;

              return {
                adults_number: numberOfAdults.toString(),
                arrival_port: sanitizeAnalyticsValue(cabinLoggedUser?.cruise?.arrivalPort), //port of disembarkation
                booking_id: sanitizeAnalyticsValue(booking?.number), //booking
                cabin_accessible: cabinLoggedUser?.cruise?.isAccessibilityFriendly ? 'yes' : 'no', //yes if the cabin is accessible, otherwise no
                cabin_connected: 'no', //yes if the cabin is connected to another one, otherwise no
                cabin_num: getActiveCruiseCabinCount(booking),
                cabin_preference_selected: 'real',
                cabin_type_detail: sanitizeAnalyticsValue(cabinDetails?.cabinCategory),
                coupon: '',
                creative_name: '', //TBC The name of a creative used in a promotional spot
                creative_slot: '', //TBC The name of a creative slot ???
                cruise_starting_date: cabinLoggedUser?.cruise?.departureDate,
                cruise_ending_date: cabinLoggedUser?.cruise?.arrivalDate,
                cruise_length: cabinLoggedUser?.cruise?.numberOfSailingDays, //number of nights
                cruise_title: sanitizeAnalyticsValue(
                  getItineraryNameByCanvas(cabinLoggedUser?.cruise?.demographicArea ?? '')
                ), //itinerary title
                cruise_type:
                  cabinLoggedUser?.cruise?.departurePort === cabinLoggedUser?.cruise?.arrivalPort
                    ? 'roundtrip'
                    : 'oneway', //oneway, roundtrip
                cruise_canvas: getSeasonFromDepartureDate(cabinLoggedUser?.cruise?.departureDate), //cruise canvas
                cruise_promo_badge: sanitizeAnalyticsValue(card?.promoPills), //text in badges separated by pipe
                currency: getCurrencyCode().toLowerCase(),
                departing_port: sanitizeAnalyticsValue(
                  cabinLoggedUser?.cruise?.departurePort?.code
                ), //port of embarkation
                discount: discountValue, //monetary value of the discount
                index: card.index, //index of the item in the list
                item_creative: 'prepaid card',
                item_id: sanitizeAnalyticsValue(card.code),
                item_name: sanitizeAnalyticsValue(
                  decodeHTMLEntities(card.description).toLowerCase()
                ),
                item_brand: sanitizeAnalyticsValue(
                  getShipCodeFromCruiseIdPA(cabinLoggedUser?.cruise?.id)
                ), //Ship code
                item_category: 'prepaid',
                item_category2: sanitizeAnalyticsValue(effectiveItemCategory2), //excursion || internet photos and other || spa beauty || specialty restaurant || beverages
                item_category3: sanitizeAnalyticsValue(
                  cabinDetails?.cabinCategory?.toLowerCase() ?? ''
                ), //cabin macrocategory
                item_category4: getExperienceTitle(booking, cabinDetails), //cabin category
                item_list_name: sanitizeAnalyticsValue(
                  buildItemListNameValue(effectiveItemCategory2, extraData?.carousel_name)
                ), //listing - [virtual_page_name] - [carousel_name]
                item_variant: 'prepaid',
                package_selected: sanitizeAnalyticsValue(cabinLoggedUser?.priceDescription),
                price: parseFloat(getPriceFromPriceBasis(card, '', '').price), //price of the item
                number_passengers: cabinLoggedUser?.passengers?.length.toString(), //number of passengers related to the product
                price_name: sanitizeAnalyticsValue(cabinLoggedUser?.priceDescription), //price name
                price_type: sanitizeAnalyticsValue(cabinLoggedUser?.priceType), //price type
                price_code: sanitizeAnalyticsValue(cabinLoggedUser?.priceCode), //price code
                promotion_id: sanitizeAnalyticsValue(card.promotion?.id),
                promotion_name: sanitizeAnalyticsValue(card.promotion?.description),
                pax_type: '', //pax type
                quantity: alreadyBookedPassengerQuantity,
                strikethrough: Number(discountValue) > 0 ? 'yes' : 'no', //yes if the price is strikethrough, otherwise no
                voyagers_club: Number(membershipData?.memberships?.length) > 0 ? 'yes' : 'no',
              };
            }),
          ],
        },
      };
      console.log('Data to be pushed:', viewItemListData);
      window.adobeDataLayer?.push(viewItemListData);
      console.log('Custom event view_item_list pushed into the data layer');
    });
  }
}

export function triggerViewItemEvent(
  card: AnalyticsCard | undefined,
  booking: GetBookingMyMscResponse,
  extraData?: {
    carousel_name?: string;
    promoBadges?: PillConfigurations[];
    itemCategory2?: string;
    shopResponse?: ShopAnalyticsResponse;
  } | null,
  cabinDetails?: CabinCategoryCfmModel | null,
  membershipData?: RetrieveVoyagerClubInfoMyMSCResponse | null
) {
  if (extraData?.shopResponse || JSON.stringify(card) !== JSON.stringify(previousCardsForItem)) {
    const cabinIDOfTheLoggedUser = getCabinIdOfTheLoggedUser(booking);
    const cabinLoggedUser: CabinI | undefined = cabinOfTheLoggedUser(
      booking?.cabins,
      cabinIDOfTheLoggedUser
    );
    const numberOfAdults = getNumberOfAdultsInCabin(cabinLoggedUser?.passengers, booking);
    const cruisePromoBadge = extraData?.promoBadges?.length
      ? extraData.promoBadges.map((badge) => badge.pillLabel.toLowerCase().trim()).join(':')
      : '';
    const effectiveItemCategory2 = extraData?.itemCategory2?.length
      ? extraData.itemCategory2
      : isOBSObjI(card)
      ? card.type.code
      : 'excursions';
    const discountValue =
      getPriceDiscountValue(getShopResponseUnitPriceDetails(card, extraData?.shopResponse), true) ??
      getDiscountValue(card, true);
    previousCardsForItem = card;
    window.didomiOnReady = window.didomiOnReady || [];
    console.log('Didomi Wrapper triggerViewItem - About to push callback');
    window.didomiOnReady.push(() => {
      console.log('Didomi Wrapper triggerViewItemEvent called - Didomi is ready');
      console.log('didomiVendorsEnabled =', window.didomiState?.didomiVendorsEnabled);
      const ecommerceNullData = { ecommerce: null };
      console.log('Data to be pushed:', ecommerceNullData);
      window.adobeDataLayer?.push(ecommerceNullData);
      const viewItemData = {
        event: 'view_item',
        area_of_payment: 'personal area',
        ecommerce: {
          items: [
            {
              adults_number: numberOfAdults.toString(),
              arrival_port: sanitizeAnalyticsValue(cabinLoggedUser?.cruise?.arrivalPort), //port of disembarkation
              booking_id: sanitizeAnalyticsValue(booking?.number), //booking
              cabin_accessible: cabinLoggedUser?.cruise?.isAccessibilityFriendly ? 'yes' : 'no', //yes if the cabin is accessible, otherwise no
              cabin_connected: 'no', //yes if the cabin is connected to another one, otherwise no
              cabin_num: getActiveCruiseCabinCount(booking),
              cabin_preference_selected: 'real',
              cabin_type_detail: sanitizeAnalyticsValue(cabinDetails?.cabinCategory),
              coupon: '',
              creative_name: '', //TBC The name of a creative used in a promotional spot
              creative_slot: '', //TBC The name of a creative slot ???
              cruise_starting_date: cabinLoggedUser?.cruise?.departureDate,
              cruise_ending_date: cabinLoggedUser?.cruise?.arrivalDate,
              cruise_length: cabinLoggedUser?.cruise?.numberOfSailingDays, //number of nights
              cruise_title: sanitizeAnalyticsValue(
                getItineraryNameByCanvas(cabinLoggedUser?.cruise?.demographicArea ?? '')
              ), //itinerary title
              cruise_type:
                cabinLoggedUser?.cruise?.departurePort === cabinLoggedUser?.cruise?.arrivalPort
                  ? 'roundtrip'
                  : 'oneway', //oneway, roundtrip
              cruise_canvas: getSeasonFromDepartureDate(cabinLoggedUser?.cruise?.departureDate), //cruise canvas
              cruise_promo_badge: sanitizeAnalyticsValue(cruisePromoBadge), //text in badges separated by pipe
              currency: getCurrencyCode().toLowerCase(),
              departing_port: sanitizeAnalyticsValue(cabinLoggedUser?.cruise?.departurePort?.code), //port of embarkation
              discount: discountValue, //monetary value of the discount
              index: getPageListIndex(card), //index of the item in the list
              item_creative: 'prepaid card',
              item_id: sanitizeAnalyticsValue(card?.code),
              item_name: sanitizeAnalyticsValue(decodeHTMLEntities(card?.description)),
              item_brand: sanitizeAnalyticsValue(
                getShipCodeFromCruiseIdPA(cabinLoggedUser?.cruise?.id)
              ), //Ship code
              item_category: 'prepaid',
              item_category2: sanitizeAnalyticsValue(effectiveItemCategory2), //excursion || internet photos and other || spa beauty || specialty restaurant || beverages
              item_category3: sanitizeAnalyticsValue(cabinDetails?.cabinCategory), //cabin macrocategory
              item_category4: getExperienceTitle(booking, cabinDetails), //cabin category
              item_list_name: sanitizeAnalyticsValue(
                buildItemListNameValue(effectiveItemCategory2, extraData?.carousel_name)
              ), //listing - [virtual_page_name] - [carousel_name]
              item_variant: 'prepaid',
              package_selected: sanitizeAnalyticsValue(cabinLoggedUser?.priceDescription),
              price: getViewItemPrice(card, extraData?.shopResponse), //price of the item
              number_passengers: cabinLoggedUser?.passengers?.length.toString(), //number of passengers related to the product
              price_name: sanitizeAnalyticsValue(cabinLoggedUser?.priceDescription), //price name
              price_type: sanitizeAnalyticsValue(cabinLoggedUser?.priceType), //price type
              price_code: sanitizeAnalyticsValue(cabinLoggedUser?.priceCode), //price code
              promotion_id: sanitizeAnalyticsValue(''), // The ID of a promotion
              promotion_name: sanitizeAnalyticsValue(''), // The name of a promotion
              pax_type: '', //pax type
              quantity: 0,
              strikethrough: Number(discountValue) > 0 ? 'yes' : 'no', //yes if the price is strikethrough, otherwise no
              voyagers_club: Number(membershipData?.memberships?.length) > 0 ? 'yes' : 'no',
            },
          ],
        },
      };
      console.log('Data to be pushed:', viewItemData);
      window.adobeDataLayer?.push(viewItemData);
      console.log('Custom event view_item pushed into the data layer');
    });
  }
}

export function triggerClickCtaEvent({
  cta_dest_link = '',
  click_main_name,
  container_info = '',
  element_position,
  element_type,
  click_location,
}: ClickCTAParams): void {
  window.didomiOnReady = window.didomiOnReady || [];
  console.log('Didomi Wrapper triggerClickCta - About to push callback');
  window.didomiOnReady.push(() => {
    console.log('Didomi Wrapper triggerClickCtaEvent called - Didomi is ready');
    console.log('didomiVendorsEnabled =', window.didomiState?.didomiVendorsEnabled);
    const clickCtaData = {
      event: 'click_cta',
      cta_dest_link: cta_dest_link,
      click_main_name: sanitizeAnalyticsValue(click_main_name),
      container_info: container_info,
      element_position: element_position,
      element_type: element_type,
      click_location: click_location,
    };
    console.log('Data to be pushed:', clickCtaData);
    window.adobeDataLayer?.push(clickCtaData);
    console.log('Custom event click_cta pushed into the data layer');
  });
}

export function triggerPAFiltersEvent(filters_selection: string) {
  window.didomiOnReady = window.didomiOnReady || [];
  console.log('Didomi Wrapper triggerPAFilters - About to push callback');
  window.didomiOnReady.push(() => {
    console.log('Didomi Wrapper triggerPAFiltersEvent called - Didomi is ready');
    console.log('didomiVendorsEnabled =', window.didomiState?.didomiVendorsEnabled);
    const paFiltersData = {
      event: 'pa_filters',
      filters_selection: filters_selection,
    };
    console.log('Data to be pushed:', paFiltersData);
    window.adobeDataLayer?.push(paFiltersData);
    console.log('Custom event pa_filters pushed into the data layer');
  });
}

export function triggerDiningReservationEvent(params: DiningReservationAnalyticsParams) {
  window.didomiOnReady = window.didomiOnReady || [];
  console.log('Didomi Wrapper triggerDiningReservation - About to push callback');
  window.didomiOnReady.push(() => {
    console.log('Didomi Wrapper triggerDiningReservationEvent called - Didomi is ready');
    console.log('didomiVendorsEnabled =', window.didomiState?.didomiVendorsEnabled);
    console.log('Data to be pushed:', params);
    window.adobeDataLayer?.push(params);
    console.log(`Custom event ${params.event} pushed into the data layer`);
  });
}

export function triggerBookingStatusEvent(
  bookingData: GetBookingMyMscResponse | null,
  activeSessionMode: LoginStatusAnalyticsEnum.FULL_LOGIN | LoginStatusAnalyticsEnum.LIGHT_LOGIN,
  cabinExperienceLabels: string | undefined,
  checkinStatus: WebCheckInStatusEnum | null,
  activeBookingId: string,
  voyagersClubPoints = 0,
  allCruisesID?: string[],
  goldenId?: string
) {
  if (!consumePendingBookingStatusAnalytics()) {
    return;
  }

  const obsQuantities = getOnboardServiceAnalyticsQuantities(bookingData?.onboardServices);
  const prepaidBookings = bookingData?.prepaid?.bookings ?? [];
  const prepaidExcursionsQuantity = prepaidBookings.flatMap(
    (booking) => booking.excursions ?? []
  ).length;
  const prepaidObsQuantities = getOnboardServiceAnalyticsQuantities(
    prepaidBookings.flatMap((booking) => booking.onboardServices ?? [])
  );
  const isBookingOptional = bookingData?.status.code === BookingStatusEnum.OPTIONAL;
  const totalPrice =
    Number(bookingData?.totalWithoutPrepaid?.due?.gross) > 0
      ? Number(bookingData?.totalWithoutPrepaid?.due?.gross)
      : Number(bookingData?.total?.due?.gross);

  let transactionStatus: string;
  if (!bookingData) {
    transactionStatus = TransactionStatusEnum.NO_BOOKING;
  } else if (isBookingOptional) {
    transactionStatus = TransactionStatusEnum.OPTIONAL;
  } else if (totalPrice && totalPrice > 0) {
    transactionStatus = TransactionStatusEnum.DEPOSIT;
  } else {
    transactionStatus = TransactionStatusEnum.FULL;
  }

  const activeBookingDepartureDate = bookingData
    ? LoginStatusAnalyticsEnum.LIGHT_LOGIN
      ? bookingData.departureDate
      : dayjs(activeBookingId.substring(2, 10), 'YYYYMMDD').valueOf()
    : 'no booking';
  const daysUntil = bookingData
    ? activeSessionMode === LoginStatusAnalyticsEnum.LIGHT_LOGIN
      ? countDaysUntil(undefined, bookingData.departureDate)
      : countDaysUntil(undefined, activeBookingDepartureDate.toString())
    : 'no booking';

  const futureCruisesCount =
    activeSessionMode === LoginStatusAnalyticsEnum.LIGHT_LOGIN
      ? 1
      : allCruisesID?.filter((booking) => {
          const bookingDate = dayjs(booking.substring(2, 10), 'YYYYMMDD');
          const activeDate = dayjs(activeBookingDepartureDate);
          return bookingDate.isAfter(activeDate) || bookingDate.isSame(activeDate);
        }).length;

  const bookingQuantity =
    activeSessionMode === LoginStatusAnalyticsEnum.LIGHT_LOGIN ? 1 : allCruisesID?.length;

  window.didomiOnReady = window.didomiOnReady || [];
  console.log('Didomi Wrapper triggerBookingStatus - About to push callback');
  window.didomiOnReady.push(() => {
    console.log('Didomi Wrapper triggerBookingStatusEvent called - Didomi is ready');
    console.log('didomiVendorsEnabled =', window.didomiState?.didomiVendorsEnabled);
    const bookingStatusData = {
      event: 'booking_status',
      login_method: activeSessionMode,
      golden_id: goldenId ?? '',
      booking_quantity: bookingQuantity ?? 0,
      window_to_next_cruise: daysUntil.toString(),
      future_cruises: futureCruisesCount?.toString() ?? 'no booking', //The number of future cruises the client has purchased. “no booking” must be used if the user doesn’t have any booking
      checkin_status: checkinStatus || 'no booking',
      voyagers_club_points: voyagersClubPoints.toString(),
      booking_id: activeBookingId ?? 'no booking',
      channel: bookingData?.bookingChannel ?? 'no booking',
      transaction_status: transactionStatus,
      cruise_starting_date: activeBookingDepartureDate,
      experience_level: cabinExperienceLabels
        ? sanitizeAnalyticsValue(cabinExperienceLabels)
        : 'no booking', //The experience related to the booking. “no booking” must be used if the user doesn’t have any booking
      staterooms_quantity: getActiveCruiseCabinCount(bookingData, 0),
      prepaid_bf: {
        //Tracks the number of times a prepaid that belongs to a specific category was purchased in the booking funnel, related to that booking id (which include those added automatically depending on the selected packages or added manually to the cart by the user)
        excursions: bookingData?.excursions?.length ?? 0,
        internet_photos_and_others: obsQuantities.internetPhotosAndOthers,
        beverages: obsQuantities.beverages,
        specialty_restaurants: obsQuantities.specialtyRestaurants,
        spa_beauty: obsQuantities.spaBeauty,
      },
      prepaid_pa: {
        excursions: prepaidExcursionsQuantity ?? 0,
        internet_photos_and_others: prepaidObsQuantities.internetPhotosAndOthers,
        beverages: prepaidObsQuantities.beverages,
        specialty_restaurants: prepaidObsQuantities.specialtyRestaurants,
        spa_beauty: prepaidObsQuantities.spaBeauty,
      },
    };
    console.log('Data to be pushed:', bookingStatusData);
    window.adobeDataLayer?.push(bookingStatusData);
    console.log('Custom event booking_status pushed into the data layer');
  });
}

export function triggerAddToCartEvent(
  card: AnalyticsCard | undefined | null,
  booking: GetBookingMyMscResponse | undefined,
  cabinDetails?: CabinCategoryCfmModel | null,
  currency?: string,
  value?: string,
  extraData?: {
    promoBadges?: PillConfigurations[];
    itemCategory2?: string;
    carouselName?: string;
    passengersToAdd?: Record<string, Record<string, boolean>> | null;
  },
  membershipData?: RetrieveVoyagerClubInfoMyMSCResponse | null
): void {
  const cabinIDOfTheLoggedUser = getCabinIdOfTheLoggedUser(booking);
  const cabinLoggedUser: CabinI | undefined = cabinOfTheLoggedUser(
    booking?.cabins,
    cabinIDOfTheLoggedUser
  );
  const numberOfAdults = getNumberOfAdultsInCabin(cabinLoggedUser?.passengers, booking);
  const cruisePromoBadge = extraData?.promoBadges?.length
    ? extraData.promoBadges.map((badge) => badge.pillLabel.toLowerCase().trim()).join(':')
    : '';
  const effectiveItemCategory2 = extraData?.itemCategory2?.length
    ? extraData.itemCategory2
    : isOBSObjI(card)
    ? card.type.code
    : 'excursions';
  const discountValue = getDiscountValue(card, true);
  window.didomiOnReady = window.didomiOnReady || [];
  console.log('Didomi Wrapper triggerAddToCart - About to push callback');
  window.didomiOnReady.push(() => {
    console.log('Didomi Wrapper triggerAddToCartEvent called - Didomi is ready');
    console.log('didomiVendorsEnabled =', window.didomiState?.didomiVendorsEnabled);
    const ecommerceNullData = { ecommerce: null };
    console.log('Data to be pushed:', ecommerceNullData);
    window.adobeDataLayer?.push(ecommerceNullData);
    const addToCartData = {
      event: 'add_to_cart',
      area_of_payment: 'personal area',
      ecommerce: {
        currency: currency?.toLowerCase() || '',
        value: parseFloat(value ?? '0'),
        items: [
          {
            adults_number: numberOfAdults.toString(),
            arrival_port: sanitizeAnalyticsValue(cabinLoggedUser?.cruise?.arrivalPort),
            booking_id: sanitizeAnalyticsValue(booking?.number),
            cabin_accessible: cabinLoggedUser?.cruise?.isAccessibilityFriendly ? 'yes' : 'no',
            cabin_connected: 'no', //yes if the cabin is connected to another one, otherwise no
            cabin_num: getActiveCruiseCabinCount(booking),
            cabin_preference_selected: 'real',
            cabin_type_detail: sanitizeAnalyticsValue(cabinDetails?.cabinCategory),
            creative_name: '', //TBC The name of a creative used in a promotional spot
            creative_slot: '', //TBC The name of a creative slot ???
            coupon: '',
            cruise_canvas: getSeasonFromDepartureDate(cabinLoggedUser?.cruise?.departureDate),
            cruise_ending_date: cabinLoggedUser?.cruise?.arrivalDate,
            cruise_length: cabinLoggedUser?.cruise?.numberOfSailingDays, //number of nights
            cruise_promo_badge: sanitizeAnalyticsValue(cruisePromoBadge), //text in badges separated by pipe
            currency: getCurrencyCode().toLowerCase(),
            cruise_starting_date: cabinLoggedUser?.cruise?.departureDate,
            cruise_title: sanitizeAnalyticsValue(
              getItineraryNameByCanvas(cabinLoggedUser?.cruise?.demographicArea ?? '')
            ), //itinerary title
            cruise_type:
              cabinLoggedUser?.cruise?.departurePort === cabinLoggedUser?.cruise?.arrivalPort
                ? 'roundtrip'
                : 'oneway', //oneway, roundtrip
            departing_port: sanitizeAnalyticsValue(cabinLoggedUser?.cruise?.departurePort?.code), //port of embarkation
            discount: discountValue, //monetary value of the discount
            index: getPageListIndex(card), //index of the item in the list
            item_brand: sanitizeAnalyticsValue(
              getShipCodeFromCruiseIdPA(cabinLoggedUser?.cruise?.id)
            ), //Ship code
            item_category: 'prepaid',
            item_category2: sanitizeAnalyticsValue(effectiveItemCategory2), //excursion || internet photos and other || spa beauty || specialty restaurant || beverages
            item_category3: sanitizeAnalyticsValue(cabinDetails?.cabinCategory),
            item_category4: getExperienceTitle(booking, cabinDetails), //cabin category
            item_list_name: sanitizeAnalyticsValue(defineItemListName(card, extraData)), //listing - [virtual_page_name] - [carousel_name]
            item_creative: 'prepaid card',
            item_id: sanitizeAnalyticsValue(card?.code),
            item_name: sanitizeAnalyticsValue(decodeHTMLEntities(card?.description)),
            item_variant: 'prepaid',
            number_passengers: cabinLoggedUser?.passengers?.length.toString(),
            package_selected: sanitizeAnalyticsValue(cabinLoggedUser?.priceDescription),
            pax_type: '', //pax type
            price: getCardUnitPrice(card, value),
            price_code: sanitizeAnalyticsValue(cabinLoggedUser?.priceCode), //price code
            price_name: sanitizeAnalyticsValue(cabinLoggedUser?.priceDescription), //price name
            price_type: sanitizeAnalyticsValue(cabinLoggedUser?.priceType),
            promotion_id: sanitizeAnalyticsValue(''), // The ID of a promotion
            promotion_name: sanitizeAnalyticsValue(''), // The name of a promotion
            quantity: getSelectedPassengerQuantityFromSelection(extraData?.passengersToAdd),
            strikethrough: Number(discountValue) > 0 ? 'yes' : 'no', //yes if the price is strikethrough, otherwise no
            voyagers_club: Number(membershipData?.memberships?.length) > 0 ? 'yes' : 'no',
          },
        ],
      },
    };
    console.log('Data to be pushed:', addToCartData);
    window.adobeDataLayer?.push(addToCartData);
    console.log('Custom event add_to_cart pushed into the data layer');
  });
}

export function triggerPurchaseEvent(purchaseData: {
  booking: GetBookingMyMscResponse;
  items: EnrichedAnalyticsCard[];
  loggedUserId: string;
  transactionId: string;
  transactionType: string;
  paymentType: string;
  paymentAmount: string;
  coupon?: string | null;
  membershipData?: RetrieveVoyagerClubInfoMyMSCResponse | null;
  cabinDetails?: CabinCategoryCfmModel | null;
  totalDiscount?: number;
  eventName?: string;
  checkoutPaymentEmail?: string;
}): void {
  const {
    booking,
    items,
    loggedUserId,
    transactionId,
    transactionType,
    paymentType,
    paymentAmount,
    membershipData,
    coupon,
    cabinDetails,
    totalDiscount = 0,
    eventName = 'purchase',
  } = purchaseData;

  const cabinIDOfTheLoggedUser = getCabinIdOfTheLoggedUser(booking);
  const cabinLoggedUser: CabinI | undefined = cabinOfTheLoggedUser(
    booking?.cabins,
    cabinIDOfTheLoggedUser
  );
  const numberOfAdults = getNumberOfAdultsInCabin(cabinLoggedUser?.passengers, booking);

  const activePassenger = booking.passengers?.find((passenger) => passenger.id === loggedUserId);
  const gender = activePassenger?.gender || '';

  const ageGroup = getAgeGroup(activePassenger?.dateOfBirth);
  const emailForTransaction = activePassenger?.email || purchaseData.checkoutPaymentEmail || '';
  const transactionEmail = emailForTransaction ? saltedHash(emailForTransaction) : '';

  const ecommerceItems = items.map((card, index) => {
    const discountValue = getDiscountValue(card);
    const effectiveItemCategory2 = card?.itemCategory2?.length
      ? card.itemCategory2
      : isOBSObjI(card)
      ? card.type.code
      : 'excursions';
    return {
      adults_number: numberOfAdults.toString(),
      arrival_port: sanitizeAnalyticsValue(cabinLoggedUser?.cruise?.arrivalPort),
      booking_id: sanitizeAnalyticsValue(booking?.number),
      cabin_accessible: cabinLoggedUser?.cruise?.isAccessibilityFriendly ? 'yes' : 'no',
      cabin_connected: 'no', //yes if the cabin is connected to another one, otherwise no
      cabin_num: getActiveCruiseCabinCount(booking),
      coupon: coupon || '',
      creative_name: '', //TBC The name of a creative used in a promotional spot
      creative_slot: '', //TBC The name of a creative slot ???
      cruise_starting_date: cabinLoggedUser?.cruise?.departureDate,
      cruise_ending_date: cabinLoggedUser?.cruise?.arrivalDate,
      cruise_length: cabinLoggedUser?.cruise?.numberOfSailingDays, // number of nights
      cruise_title: sanitizeAnalyticsValue(
        getItineraryNameByCanvas(cabinLoggedUser?.cruise?.demographicArea ?? '')
      ), //itinerary title
      cruise_type:
        cabinLoggedUser?.cruise?.departurePort === cabinLoggedUser?.cruise?.arrivalPort
          ? 'roundtrip'
          : 'oneway', //oneway, roundtrip
      cruise_canvas: getSeasonFromDepartureDate(cabinLoggedUser?.cruise?.departureDate), //cruise canvas
      cruise_promo_badge: sanitizeAnalyticsValue(getPromoBadgeFromCart(card?.code)), //text in badges separated by pipe
      currency: getCurrencyCode().toLowerCase(),
      departing_port: sanitizeAnalyticsValue(cabinLoggedUser?.cruise?.departurePort?.code), //port of embarkation
      discount: discountValue, //monetary value of the discount
      index: index + 1, //index of the item in the list
      item_brand: sanitizeAnalyticsValue(getShipCodeFromCruiseIdPA(cabinLoggedUser?.cruise?.id)), //Ship code
      item_category: 'prepaid',
      item_category2: sanitizeAnalyticsValue(effectiveItemCategory2), //excursion || internet photos and other || spa beauty || specialty restaurant || beverages
      item_category3: sanitizeAnalyticsValue(cabinDetails?.cabinCategory), //cabin macrocategory
      item_category4: getExperienceTitle(booking, cabinDetails), //cabin category
      item_list_name: sanitizeAnalyticsValue(resolveItemListName(card, effectiveItemCategory2)), //listing - [virtual_page_name] - [carousel_name]
      item_id: sanitizeAnalyticsValue(card?.code),
      item_name: sanitizeAnalyticsValue(decodeHTMLEntities(card?.description)),
      item_variant: 'prepaid',
      price: parseFloat(getPriceFromPriceBasis(card, '', '', '', true).price), //price of the item
      number_passengers: cabinLoggedUser?.passengers?.length.toString(), //number of passengers related to the product
      price_name: sanitizeAnalyticsValue(cabinLoggedUser?.priceDescription),
      price_type: sanitizeAnalyticsValue(cabinLoggedUser?.priceType),
      price_code: sanitizeAnalyticsValue(cabinLoggedUser?.priceCode),
      promotion_id: sanitizeAnalyticsValue(card?.promotion?.id),
      promotion_name: sanitizeAnalyticsValue(card?.promotion?.description),
      package_selected: sanitizeAnalyticsValue(cabinLoggedUser?.priceDescription),
      cabin_preference_selected: 'real',
      cabin_type_detail: sanitizeAnalyticsValue(cabinDetails?.cabinCategory),
      pax_type: '', //pax type
      quantity: card?.selectedPassengerQuantity || getSelectedPassengerQuantityFromCart(card),
      strikethrough: Number(discountValue) > 0 ? 'yes' : 'no', //yes if the price is strikethrough, otherwise no
      voyagers_club: Number(membershipData?.memberships?.length) > 0 ? 'yes' : 'no',
      item_creative: 'prepaid card',
    };
  });

  window.didomiOnReady = window.didomiOnReady || [];
  console.log('Didomi Wrapper triggerPurchase - About to push callback');
  window.didomiOnReady.push(() => {
    console.log('Didomi Wrapper triggerPurchaseEvent called - Didomi is ready');
    console.log('didomiVendorsEnabled =', window.didomiState?.didomiVendorsEnabled);
    const ecommerceNullData = { ecommerce: null };
    console.log('Data to be pushed:', ecommerceNullData);
    window.adobeDataLayer?.push(ecommerceNullData);
    const purchaseData = {
      event: eventName,
      area_of_payment: 'personal area',
      funnel_order: 'summary:checkout:thankyou page',
      step_name: 'thankyou page',
      step_number: '3',
      gender: gender,
      age_group: ageGroup,
      booking_id: booking?.number || '',
      transaction_type: transactionType,
      payment_type: paymentType,
      transaction_email: transactionEmail,
      ecommerce: {
        transaction_id: transactionId,
        value: parseFloat(paymentAmount || '0'),
        currency: getCurrencyCode().toLowerCase(),
        coupon: coupon || '',
        total_discount: Number(totalDiscount.toFixed(2)),
        base_deposit_amount: 0,
        items: ecommerceItems,
      },
    };
    console.log('Data to be pushed:', purchaseData);
    window.adobeDataLayer?.push(purchaseData);
    console.log(`Custom event ${eventName} pushed into the data layer`);
  });
}

export function triggerPaymentFailureEvent(data: {
  booking: GetBookingMyMscResponse;
  items: EnrichedAnalyticsCard[];
  loggedUserId: string;
  transactionId: string;
  transactionType: string;
  paymentType: string;
  paymentAmount: string;
  coupon?: string | null;
  membershipData?: RetrieveVoyagerClubInfoMyMSCResponse | null;
  cabinDetails?: CabinCategoryCfmModel | null;
  totalDiscount?: number;
  checkoutPaymentEmail?: string;
}): void {
  triggerPurchaseEvent({ ...data, eventName: 'payment_failure' });
}

export function triggerAddPaymentInfoEvent(params: {
  booking: GetBookingMyMscResponse | undefined;
  loggedUserId: string;
  items: EnrichedAnalyticsCard[];
  cabinDetails?: CabinCategoryCfmModel | null;
  currency?: string;
  value?: string;
  mopAvailable: string;
  appliedECoupon?: string | null;
  membershipData?: RetrieveVoyagerClubInfoMyMSCResponse | null;
}): void {
  const {
    booking,
    loggedUserId,
    items,
    cabinDetails,
    currency,
    value,
    mopAvailable,
    appliedECoupon,
    membershipData,
  } = params;

  const cabinIDOfTheLoggedUser = getCabinIdOfTheLoggedUser(booking);
  const cabinLoggedUser: CabinI | undefined = cabinOfTheLoggedUser(
    booking?.cabins,
    cabinIDOfTheLoggedUser
  );
  const numberOfAdults = booking
    ? getNumberOfAdultsInCabin(cabinLoggedUser?.passengers, booking)
    : 0;

  const activePassenger = booking?.passengers?.find((passenger) => passenger.id === loggedUserId);
  const gender = activePassenger?.gender || '';

  const ageGroup = getAgeGroup(activePassenger?.dateOfBirth);

  const ecommerceItems = items.map((card, index) => {
    const discountValue = getDiscountValue(card);
    const effectiveItemCategory2 = card?.itemCategory2?.length
      ? card.itemCategory2
      : isOBSObjI(card)
      ? card.type.code
      : 'excursions';
    return {
      adults_number: numberOfAdults.toString(),
      arrival_port: sanitizeAnalyticsValue(cabinLoggedUser?.cruise?.arrivalPort),
      booking_id: sanitizeAnalyticsValue(booking?.number),
      cabin_accessible: cabinLoggedUser?.cruise?.isAccessibilityFriendly ? 'yes' : 'no',
      cabin_connected: 'no', //yes if the cabin is connected to another one, otherwise no
      cabin_num: getActiveCruiseCabinCount(booking),
      cabin_preference_selected: 'real',
      cabin_type_detail: sanitizeAnalyticsValue(cabinDetails?.cabinCategory),
      coupon: appliedECoupon || '',
      creative_name: '', //TBC The name of a creative used in a promotional spot
      creative_slot: '', //TBC The name of a creative slot ???
      cruise_starting_date: cabinLoggedUser?.cruise?.departureDate,
      cruise_ending_date: cabinLoggedUser?.cruise?.arrivalDate,
      cruise_length: cabinLoggedUser?.cruise?.numberOfSailingDays, //number of nights
      cruise_title: sanitizeAnalyticsValue(
        getItineraryNameByCanvas(cabinLoggedUser?.cruise?.demographicArea ?? '')
      ), //itinerary title
      cruise_type:
        cabinLoggedUser?.cruise?.departurePort === cabinLoggedUser?.cruise?.arrivalPort
          ? 'roundtrip'
          : 'oneway', //oneway, roundtrip
      cruise_canvas: getSeasonFromDepartureDate(cabinLoggedUser?.cruise?.departureDate), //cruise canvas
      cruise_promo_badge: sanitizeAnalyticsValue(getPromoBadgeFromCart(card?.code)), //text in badges separated by pipe
      currency: getCurrencyCode().toLowerCase(),
      departing_port: sanitizeAnalyticsValue(cabinLoggedUser?.cruise?.departurePort?.code), //port of embarkation
      discount: discountValue, //monetary value of the discount
      index: index + 1, //index of the item in the list
      item_brand: sanitizeAnalyticsValue(getShipCodeFromCruiseIdPA(cabinLoggedUser?.cruise?.id)), //Ship code
      item_category: 'prepaid',
      item_category2: sanitizeAnalyticsValue(effectiveItemCategory2), //excursion || internet photos and other || spa beauty || specialty restaurant || beverages
      item_category3: sanitizeAnalyticsValue(cabinDetails?.cabinCategory), //cabin macrocategory
      item_category4: getExperienceTitle(booking, cabinDetails), //cabin category
      item_list_name: sanitizeAnalyticsValue(resolveItemListName(card, effectiveItemCategory2)), //listing - [virtual_page_name] - [carousel_name]
      item_id: sanitizeAnalyticsValue(card?.code),
      item_name: sanitizeAnalyticsValue(decodeHTMLEntities(card?.description)),
      item_variant: 'prepaid',
      package_selected: sanitizeAnalyticsValue(cabinLoggedUser?.priceDescription),
      price: parseFloat(getPriceFromPriceBasis(card, '', '', '', true).price), //price of the item
      number_passengers: cabinLoggedUser?.passengers?.length.toString(), //number of passengers related to the product
      price_name: sanitizeAnalyticsValue(cabinLoggedUser?.priceDescription), //price name
      price_type: sanitizeAnalyticsValue(cabinLoggedUser?.priceType), //price type
      price_code: sanitizeAnalyticsValue(cabinLoggedUser?.priceCode), //price code
      promotion_id: sanitizeAnalyticsValue(card?.promotion?.id), // The ID of a promotion
      promotion_name: sanitizeAnalyticsValue(card?.promotion?.description), // The name of a promotion
      pax_type: '', //pax type
      quantity: card?.selectedPassengerQuantity || getSelectedPassengerQuantityFromCart(card),
      strikethrough: Number(discountValue) > 0 ? 'yes' : 'no', //yes if the price is strikethrough, otherwise no
      voyagers_club: Number(membershipData?.memberships?.length) > 0 ? 'yes' : 'no',
      item_creative: 'prepaid card',
    };
  });

  window.didomiOnReady = window.didomiOnReady || [];
  console.log('Didomi Wrapper triggerAddPaymentInfo - About to push callback');
  window.didomiOnReady.push(() => {
    console.log('Didomi Wrapper triggerAddPaymentInfoEvent called - Didomi is ready');
    console.log('didomiVendorsEnabled =', window.didomiState?.didomiVendorsEnabled);
    const ecommerceNullData = { ecommerce: null };
    console.log('Data to be pushed:', ecommerceNullData);
    window.adobeDataLayer?.push(ecommerceNullData);
    const addPaymentInfoData = {
      event: 'add_payment_info',
      area_of_payment: 'personal area',
      funnel_order: 'summary:checkout',
      step_name: 'checkout',
      step_number: '2',
      gender: gender,
      age_group: ageGroup,
      mop_available: mopAvailable,
      ecommerce: {
        currency: (currency ?? getCurrencyCode()).toLowerCase(),
        value: parseFloat(value ?? '0'),
        items: ecommerceItems,
      },
    };
    console.log('Data to be pushed:', addPaymentInfoData);
    window.adobeDataLayer?.push(addPaymentInfoData);
    console.log('Custom event add_payment_info pushed into the data layer');
  });
}

export function triggerBookingAddPaymentInfoEvent(params: {
  booking: GetBookingMyMscResponse | undefined;
  loggedUserId: string;
  cabinDetails?: CabinCategoryCfmModel | null;
  currency?: string;
  value?: string;
  mopAvailable: string;
  appliedECoupon?: string | null;
  voyagersClub?: RetrieveVoyagerClubInfoMembership[];
}): void {
  const {
    booking,
    loggedUserId,
    cabinDetails,
    currency,
    value,
    mopAvailable,
    appliedECoupon,
    voyagersClub,
  } = params;

  const cabinIDOfTheLoggedUser = getCabinIdOfTheLoggedUser(booking);
  const cabinLoggedUser: CabinI | undefined = cabinOfTheLoggedUser(
    booking?.cabins,
    cabinIDOfTheLoggedUser
  );
  const numberOfAdults = booking
    ? getNumberOfAdultsInCabin(cabinLoggedUser?.passengers, booking)
    : 0;

  const activePassenger = booking?.passengers?.find((passenger) => passenger.id === loggedUserId);
  const gender = activePassenger?.gender || '';

  const ageGroup = getAgeGroup(activePassenger?.dateOfBirth);

  const cruiseItem = {
    index: 1,
    item_id: sanitizeAnalyticsValue(cabinLoggedUser?.cruise?.id),
    item_name: sanitizeAnalyticsValue(
      decodeHTMLEntities(cabinLoggedUser?.cruise?.itinerary?.description)
    ),
    item_brand: sanitizeAnalyticsValue(getShipCodeFromCruiseIdPA(cabinLoggedUser?.cruise?.id)), //Ship code
    item_category: 'cruiseonly',
    item_category2: 'no',
    item_category3: sanitizeAnalyticsValue(cabinDetails?.cabinCategory), //cabin macrocategory
    item_category4: getExperienceTitle(booking, cabinDetails), //cabin category
    item_list_name: sanitizeAnalyticsValue(buildItemListNameValue('cruise')), //listing - [virtual_page_name] - [carousel_name]
    item_variant: 'booking',
    currency: getCurrencyCode().toLowerCase(),
    price: parseFloat(value || '0'),
    quantity: 1,
    coupon: appliedECoupon || '',
    discount: 0, //monetary value of the discount
    creative_name: '', //TBC The name of a creative used in a promotional spot
    creative_slot: '', //TBC The name of a creative slot ???
    cruise_starting_date: cabinLoggedUser?.cruise?.departureDate,
    cruise_ending_date: cabinLoggedUser?.cruise?.arrivalDate,
    cruise_length: cabinLoggedUser?.cruise?.numberOfSailingDays, //number of nights
    cruise_title: sanitizeAnalyticsValue(
      getItineraryNameByCanvas(cabinLoggedUser?.cruise?.demographicArea ?? '')
    ), //itinerary title
    cruise_type:
      cabinLoggedUser?.cruise?.departurePort?.code === cabinLoggedUser?.cruise?.arrivalPort
        ? 'round trip'
        : 'oneway', //oneway, round trip
    cruise_canvas: getSeasonFromDepartureDate(cabinLoggedUser?.cruise?.departureDate), //cruise canvas
    cruise_promo_badge: '', //text in badges separated by pipe (no badges on payment page)
    arrival_port: sanitizeAnalyticsValue(cabinLoggedUser?.cruise?.arrivalPort), //port of disembarkation
    departing_port: sanitizeAnalyticsValue(cabinLoggedUser?.cruise?.departurePort?.code), //port of embarkation
    cabin_num: getActiveCruiseCabinCount(booking),
    cabin_accessible: cabinLoggedUser?.cruise?.isAccessibilityFriendly ? 'yes' : 'no', //yes if the cabin is accessible, otherwise no
    cabin_connected: 'no', //yes if the cabin is connected to another one, otherwise no
    cabin_preference_selected: 'real',
    cabin_type_detail: sanitizeAnalyticsValue(cabinDetails?.cabinCategory),
    adults_number: numberOfAdults.toString(),
    package_selected: sanitizeAnalyticsValue(cabinLoggedUser?.priceDescription),
    number_passengers: cabinLoggedUser?.passengers?.length.toString(), //number of passengers related to the product
    price_name: sanitizeAnalyticsValue(cabinLoggedUser?.priceDescription), //price name
    price_type: sanitizeAnalyticsValue(cabinLoggedUser?.priceType), //price type
    price_code: sanitizeAnalyticsValue(cabinLoggedUser?.priceCode), //price code
    promotion_id: sanitizeAnalyticsValue(''), // The ID of a promotion
    promotion_name: sanitizeAnalyticsValue(''), // The name of a promotion
    pax_type: '', //pax type
    strikethrough: 'no', //yes if the price is strikethrough, otherwise no
    voyagers_club: Number(voyagersClub?.length) ? 'yes' : 'no',
    item_creative: 'booking card',
    booking_id: sanitizeAnalyticsValue(booking?.number),
  };

  window.didomiOnReady = window.didomiOnReady || [];
  console.log('Didomi Wrapper triggerBookingAddPaymentInfo - About to push callback');
  window.didomiOnReady.push(() => {
    console.log('Didomi Wrapper triggerBookingAddPaymentInfoEvent called - Didomi is ready');
    console.log('didomiVendorsEnabled =', window.didomiState?.didomiVendorsEnabled);
    const ecommerceNullData = { ecommerce: null };
    console.log('Data to be pushed:', ecommerceNullData);
    window.adobeDataLayer?.push(ecommerceNullData);
    const addPaymentInfoData = {
      event: 'add_payment_info',
      area_of_payment: 'personal area',
      funnel_order: 'checkout',
      step_name: 'checkout',
      step_number: '1',
      gender: gender,
      age_group: ageGroup,
      mop_available: mopAvailable,
      ecommerce: {
        currency: (currency ?? getCurrencyCode()).toLowerCase(),
        value: parseFloat(value ?? '0'),
        items: [cruiseItem],
      },
    };
    console.log('Data to be pushed:', addPaymentInfoData);
    window.adobeDataLayer?.push(addPaymentInfoData);
    console.log('Custom event add_payment_info pushed into the data layer');
  });
}

export function triggerBookingPurchaseEvent(purchaseData: {
  booking: GetBookingMyMscResponse;
  loggedUserId: string;
  transactionId: string;
  transactionType: string | undefined;
  paymentType: string;
  paymentAmount: string;
  membershipData?: RetrieveVoyagerClubInfoMyMSCResponse | null;
  cabinDetails?: CabinCategoryCfmModel | null;
  totalDiscount?: number;
  eventName?: string;
  checkoutPaymentEmail?: string;
}): void {
  const {
    booking,
    loggedUserId,
    transactionId,
    transactionType,
    paymentType,
    paymentAmount,
    membershipData,
    cabinDetails,
    totalDiscount = 0,
    eventName = 'purchase',
  } = purchaseData;

  const cabinIDOfTheLoggedUser = getCabinIdOfTheLoggedUser(booking);
  const cabinLoggedUser: CabinI | undefined = cabinOfTheLoggedUser(
    booking?.cabins,
    cabinIDOfTheLoggedUser
  );
  const numberOfAdults = getNumberOfAdultsInCabin(cabinLoggedUser?.passengers, booking);
  const voyagersClub = Number(membershipData?.memberships?.length) > 0 ? 'yes' : 'no';
  const activePassenger = booking.passengers?.find((passenger) => passenger.id === loggedUserId);
  const gender = activePassenger?.gender || '';

  const ageGroup = getAgeGroup(activePassenger?.dateOfBirth);
  const emailForTransaction = activePassenger?.email || purchaseData.checkoutPaymentEmail || '';
  const transactionEmail = emailForTransaction ? saltedHash(emailForTransaction) : '';

  const cruiseItem = {
    index: 1,
    item_id: sanitizeAnalyticsValue(cabinLoggedUser?.cruise?.id),
    item_name: sanitizeAnalyticsValue(
      decodeHTMLEntities(cabinLoggedUser?.cruise?.itinerary?.description)
    ),
    item_brand: sanitizeAnalyticsValue(getShipCodeFromCruiseIdPA(cabinLoggedUser?.cruise?.id)),
    item_category: 'cruiseonly',
    item_category2: 'no',
    item_category3: sanitizeAnalyticsValue(cabinDetails?.cabinCategory),
    item_category4: getExperienceTitle(booking, cabinDetails),
    item_list_name: sanitizeAnalyticsValue(buildItemListNameValue('cruise')), //listing - [virtual_page_name] - [carousel_name]
    item_variant: 'booking',
    currency: getCurrencyCode().toLowerCase(),
    price: parseFloat(paymentAmount || '0'),
    quantity: 1,
    coupon: '',
    discount: Number(totalDiscount.toFixed(2)),
    creative_name: '',
    creative_slot: '',
    cruise_starting_date: cabinLoggedUser?.cruise?.departureDate,
    cruise_ending_date: cabinLoggedUser?.cruise?.arrivalDate,
    cruise_length: cabinLoggedUser?.cruise?.numberOfSailingDays,
    cruise_title: sanitizeAnalyticsValue(
      getItineraryNameByCanvas(cabinLoggedUser?.cruise?.demographicArea ?? '')
    ), //itinerary title
    cruise_type:
      cabinLoggedUser?.cruise?.departurePort?.code === cabinLoggedUser?.cruise?.arrivalPort
        ? 'round trip'
        : 'oneway',
    cruise_canvas: getSeasonFromDepartureDate(cabinLoggedUser?.cruise?.departureDate),
    cruise_promo_badge: '',
    arrival_port: sanitizeAnalyticsValue(cabinLoggedUser?.cruise?.arrivalPort),
    departing_port: sanitizeAnalyticsValue(cabinLoggedUser?.cruise?.departurePort?.code),
    cabin_num: getActiveCruiseCabinCount(booking),
    cabin_accessible: cabinLoggedUser?.cruise?.isAccessibilityFriendly ? 'yes' : 'no',
    cabin_connected: 'no',
    cabin_preference_selected: 'real',
    cabin_type_detail: sanitizeAnalyticsValue(cabinDetails?.cabinCategory),
    adults_number: numberOfAdults.toString(),
    package_selected: sanitizeAnalyticsValue(cabinLoggedUser?.priceDescription),
    number_passengers: cabinLoggedUser?.passengers?.length.toString(),
    price_name: sanitizeAnalyticsValue(cabinLoggedUser?.priceDescription),
    price_type: sanitizeAnalyticsValue(cabinLoggedUser?.priceType),
    price_code: sanitizeAnalyticsValue(cabinLoggedUser?.priceCode),
    promotion_id: sanitizeAnalyticsValue(''),
    promotion_name: sanitizeAnalyticsValue(''),
    pax_type: '',
    strikethrough: 'no',
    voyagers_club: voyagersClub,
    item_creative: 'booking card',
    booking_id: sanitizeAnalyticsValue(booking?.number),
  };

  window.didomiOnReady = window.didomiOnReady || [];
  console.log('Didomi Wrapper triggerBookingPurchase - About to push callback');
  window.didomiOnReady.push(() => {
    console.log('Didomi Wrapper triggerBookingPurchaseEvent called - Didomi is ready');
    console.log('didomiVendorsEnabled =', window.didomiState?.didomiVendorsEnabled);
    const ecommerceNullData = { ecommerce: null };
    console.log('Data to be pushed:', ecommerceNullData);
    window.adobeDataLayer?.push(ecommerceNullData);
    const purchaseData = {
      event: eventName,
      area_of_payment: 'personal area',
      funnel_order: 'checkout:thankyou page',
      step_name: 'thankyou page',
      step_number: '2',
      gender,
      age_group: ageGroup,
      booking_id: booking?.number || '',
      transaction_type: sanitizeAnalyticsValue(transactionType),
      payment_type: paymentType,
      transaction_email: transactionEmail,
      ecommerce: {
        transaction_id: transactionId,
        value: parseFloat(paymentAmount),
        currency: getCurrencyCode().toLowerCase(),
        coupon: '',
        total_discount: Number(totalDiscount.toFixed(2)),
        base_deposit_amount: Number(booking?.total?.received),
        items: [cruiseItem],
      },
    };
    console.log('Data to be pushed:', purchaseData);
    window.adobeDataLayer?.push(purchaseData);
    console.log(`Custom event ${eventName} pushed into the data layer`);
  });
}

export function triggerBookingPaymentFailureEvent(data: {
  booking: GetBookingMyMscResponse;
  loggedUserId: string;
  transactionId: string;
  transactionType: string | undefined;
  paymentType: string;
  paymentAmount: string;
  membershipData?: RetrieveVoyagerClubInfoMyMSCResponse | null;
  cabinDetails?: CabinCategoryCfmModel | null;
  totalDiscount?: number;
  checkoutPaymentEmail?: string;
}): void {
  triggerBookingPurchaseEvent({ ...data, eventName: 'payment_failure' });
}

const getViewCartDataLayerKey = (eventData: Record<string, any>): string => {
  const items = eventData.ecommerce?.items ?? [];
  if (items.length === 0) {
    return '';
  }

  const itemsKey = items
    .map((item: Record<string, any>) => `${item.item_id ?? ''}:${item.item_category2 ?? ''}`)
    .sort()
    .join('|');

  return [eventData.ecommerce?.currency ?? '', items[0]?.booking_id ?? '', itemsKey].join(':');
};

const removePreviousViewCartEvent = (currentViewCartData: Record<string, any>): void => {
  if (!Array.isArray(window.adobeDataLayer)) {
    return;
  }

  const currentKey = getViewCartDataLayerKey(currentViewCartData);

  if (!currentKey) {
    return;
  }

  const currentIndex = window.adobeDataLayer.length - 1;
  const previousViewCartIndex =
    currentIndex > 1 && window.adobeDataLayer[currentIndex - 1]?.ecommerce === null
      ? currentIndex - 2
      : currentIndex - 1;

  if (getViewCartDataLayerKey(window.adobeDataLayer[previousViewCartIndex]) === currentKey) {
    const previousIndex =
      previousViewCartIndex > 0 &&
      window.adobeDataLayer[previousViewCartIndex - 1]?.ecommerce === null
        ? previousViewCartIndex - 1
        : previousViewCartIndex;
    const deleteCount = previousIndex === previousViewCartIndex ? 1 : 2;
    window.adobeDataLayer.splice(previousIndex, deleteCount);
  }
};

export function triggerViewCartEvent(params: {
  booking: GetBookingMyMscResponse | undefined;
  items: EnrichedAnalyticsCard[];
  cabinDetails?: CabinCategoryCfmModel | null;
  currency?: string;
  value?: string;
  membershipData?: RetrieveVoyagerClubInfoMyMSCResponse | null;
}): void {
  const { booking, items, cabinDetails, currency, value, membershipData } = params;

  const cabinIDOfTheLoggedUser = getCabinIdOfTheLoggedUser(booking);
  const cabinLoggedUser: CabinI | undefined = cabinOfTheLoggedUser(
    booking?.cabins,
    cabinIDOfTheLoggedUser
  );
  const numberOfAdults = booking
    ? getNumberOfAdultsInCabin(cabinLoggedUser?.passengers, booking)
    : 0;

  const ecommerceItems = items.map((card, index) => {
    const discountValue = getDiscountValue(card);
    const effectiveItemCategory2 = card?.itemCategory2?.length
      ? card.itemCategory2
      : isOBSObjI(card)
      ? card.type.code
      : 'excursions';
    return {
      adults_number: numberOfAdults.toString(),
      arrival_port: sanitizeAnalyticsValue(cabinLoggedUser?.cruise?.arrivalPort),
      booking_id: sanitizeAnalyticsValue(booking?.number),
      cabin_accessible: cabinLoggedUser?.cruise?.isAccessibilityFriendly ? 'yes' : 'no',
      cabin_connected: 'no',
      cabin_num: getActiveCruiseCabinCount(booking),
      cabin_preference_selected: 'real',
      cabin_type_detail: sanitizeAnalyticsValue(cabinDetails?.cabinCategory),
      coupon: '',
      creative_name: '',
      creative_slot: '',
      cruise_starting_date: cabinLoggedUser?.cruise?.departureDate,
      cruise_ending_date: cabinLoggedUser?.cruise?.arrivalDate,
      cruise_length: cabinLoggedUser?.cruise?.numberOfSailingDays,
      cruise_title: sanitizeAnalyticsValue(
        getItineraryNameByCanvas(cabinLoggedUser?.cruise?.demographicArea ?? '')
      ), //itinerary title
      cruise_type:
        cabinLoggedUser?.cruise?.departurePort === cabinLoggedUser?.cruise?.arrivalPort
          ? 'roundtrip'
          : 'oneway',
      cruise_canvas: getSeasonFromDepartureDate(cabinLoggedUser?.cruise?.departureDate),
      cruise_promo_badge: sanitizeAnalyticsValue(getPromoBadgeFromCart(card?.code)),
      currency: getCurrencyCode().toLowerCase(),
      departing_port: sanitizeAnalyticsValue(cabinLoggedUser?.cruise?.departurePort?.code),
      discount: discountValue,
      index: index + 1,
      item_brand: sanitizeAnalyticsValue(getShipCodeFromCruiseIdPA(cabinLoggedUser?.cruise?.id)),
      item_category: 'prepaid',
      item_category2: sanitizeAnalyticsValue(effectiveItemCategory2),
      item_category3: sanitizeAnalyticsValue(cabinDetails?.cabinCategory),
      item_category4: getExperienceTitle(booking, cabinDetails),
      item_list_name: sanitizeAnalyticsValue(resolveItemListName(card, effectiveItemCategory2)),
      item_id: sanitizeAnalyticsValue(card?.code),
      item_name: sanitizeAnalyticsValue(decodeHTMLEntities(card?.description)),
      item_variant: 'prepaid',
      package_selected: sanitizeAnalyticsValue(cabinLoggedUser?.priceDescription),
      price: parseFloat(getPriceFromPriceBasis(card, '', '', '', true).price),
      number_passengers: cabinLoggedUser?.passengers?.length.toString(),
      price_name: sanitizeAnalyticsValue(cabinLoggedUser?.priceDescription),
      price_type: sanitizeAnalyticsValue(cabinLoggedUser?.priceType),
      price_code: sanitizeAnalyticsValue(cabinLoggedUser?.priceCode),
      promotion_id: sanitizeAnalyticsValue(card?.promotion?.id),
      promotion_name: sanitizeAnalyticsValue(card?.promotion?.description),
      pax_type: '',
      quantity: card?.selectedPassengerQuantity || getSelectedPassengerQuantityFromCart(card),
      strikethrough: Number(discountValue) > 0 ? 'yes' : 'no',
      voyagers_club: Number(membershipData?.memberships?.length) > 0 ? 'yes' : 'no',
      item_creative: 'prepaid card',
    };
  });

  window.didomiOnReady = window.didomiOnReady || [];
  console.log('Didomi Wrapper triggerViewCart - About to push callback');
  window.didomiOnReady.push(() => {
    console.log('Didomi Wrapper triggerViewCartEvent called - Didomi is ready');
    console.log('didomiVendorsEnabled =', window.didomiState?.didomiVendorsEnabled);
    const ecommerceNullData = { ecommerce: null };
    console.log('Data to be pushed:', ecommerceNullData);
    window.adobeDataLayer?.push(ecommerceNullData);
    const viewCartData = {
      event: 'view_cart',
      area_of_payment: 'personal area',
      funnel_order: 'summary',
      step_name: 'summary',
      step_number: '1',
      ecommerce: {
        currency: (currency ?? getCurrencyCode()).toLowerCase(),
        value: parseFloat(value ?? '0'),
        items: ecommerceItems,
      },
    };
    console.log('Data to be pushed:', viewCartData);
    window.adobeDataLayer?.push(viewCartData);
    removePreviousViewCartEvent(viewCartData);
    console.log('Custom event view_cart pushed into the data layer');
  });
}
